import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { createSafeStorage } from './safe-storage';

type OnboardingState = {
  /**
   * The session user the tour has already run for, or `null`. Holds a user id rather than a
   * boolean so switching account without a clean sign-out — a second operator taking over
   * the tab — still gets its own run.
   */
  completedFor: string | null;
  /** Records that the tour ended for this user — finished, dismissed, or skipped alike. */
  markCompleted: (userId: string) => void;
  /** Forgets the record, so the next sign-in runs the tour again. */
  forget: () => void;
};

/**
 * Whether the product tour has already run in this login session.
 *
 * Persisted, so a reload does not replay it, but deliberately NOT durable across logins: the
 * auth layer calls `forget()` on every path that ends a session (`use-logout.ts` for an
 * explicit sign-out, `leave-protected-ui.ts` for a sibling tab's logout and for an
 * unrecoverable refresh), so signing in again starts the tour over exactly once.
 *
 * localStorage rather than sessionStorage on purpose. sessionStorage is per tab and survives a
 * same-tab trip out to the Cognito Hosted UI and back, so it would neither reset on sign-out nor
 * keep two open tabs from each running their own copy.
 */
export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      completedFor: null,
      markCompleted: (userId) => set({ completedFor: userId }),
      forget: () => set({ completedFor: null }),
    }),
    {
      name: 'management.onboarding',
      storage: createJSONStorage(createSafeStorage),
      // Only the record is written; the actions are not state.
      partialize: (state) => ({ completedFor: state.completedFor }),
      // v0 kept a per-user map that outlived the session. Anyone carrying one starts fresh —
      // which simply means seeing the tour once more — rather than having it decoded.
      version: 1,
      migrate: () => ({ completedFor: null }),
    },
  ),
);

/** True when the tour has not yet run for this user in this login session. */
export function selectShouldShowTour(state: OnboardingState, userId: string): boolean {
  return state.completedFor !== userId;
}
